import React, { useEffect, Fragment, useCallback, useState, useRef, useMemo } from 'react';
import { Button, Select, createUseStyles, Tooltip } from '@Sage/hoi-poi-ui';

import { getCustomListOptions } from 'lib/customLists';
import { getList, getValue } from 'lib/services/valueLists';
import { useWeb5Context } from 'lib/Web5Context';
import styles from './styles';
const useStyles = createUseStyles(styles, 'List');

const CACHE = {};

const formatList = (data, valueField = 'id') =>
    data.map((item) => ({
        ...item,
        label: item.description,
        value: item[valueField] ?? item.id,
    }));

const resolveListValue = (value, options = []) => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'object') return value;

    const match = options.find(
        (option) => String(option.value).toLowerCase() === String(value).toLowerCase(),
    );

    return match || value;
};

function List({
    schema,
    table,
    id,
    loadOptions,
    loadOptionsWithFuzzy,
    value,
    placeholder,
    noOptionsPlaceholder,
    customGetList,
    onClickButtonList,
    loadedValues,
    input,
    idFieldTable,
    valueObjectNeeded,
    isValueObject,
    ...props
}) {
    // id could be the id of the field, if comes from an schema
    // idFieldTable is the name of the field where the table comes from
    const { t, language, platform, implementation } = useWeb5Context();
    const classes = useStyles();
    const [valueObject, setValueObject] = useState();
    const [tableOptions, setTableOptions] = useState([]);
    const fieldContext = schema?.context?.fields?.[id];
    const listTable = table || fieldContext?.table;
    const valueField = fieldContext?.valueField || 'id';
    const valueFromBackend = useRef(value && valueObjectNeeded);
    const platformKey = String(platform);

    const getValueObjects = useCallback(() => {
        getValue(valueObjectNeeded, value, language)
            .then(({ data }) => {
                setValueObject({
                    value: data.id,
                    label:
                        data.description ||
                        data.name ||
                        data.model ||
                        data.strName ||
                        `${data?.firstName} ${data?.lastName}`,
                });
            })
            .catch((err) => console.error(err))
            .then(() => (valueFromBackend.current = false));
    }, [language, value, valueObjectNeeded]);

    useEffect(() => {
        if (valueObjectNeeded && valueFromBackend.current) {
            getValueObjects();
        }
    }, [getValueObjects, valueObjectNeeded]);

    useEffect(() => {
        if (!value && valueObject) setValueObject(null);
        if (value && valueObject && value !== valueObject.value) setValueObject(null);
    }, [value, valueObject]);

    // This loadedValues is necessary in order to include a new option when main options have been
    // already loaded (E.g. when creating a new Value List From ExtraFields Crud)
    const finalPropOptions = useMemo(() => props.options || [], [props.options]);
    const customListOptions = useMemo(
        () => (customGetList ? null : getCustomListOptions(listTable, t)),
        [customGetList, listTable, t],
    );
    const localOptions = useMemo(
        () => (finalPropOptions.length > 0 ? finalPropOptions : customListOptions || []),
        [customListOptions, finalPropOptions],
    );
    const shouldLoadTableOptions = Boolean(listTable && localOptions.length === 0);

    const innerLoadOptions = useCallback(() => {
        const pr = new Promise((resolve) => {
            if (customGetList) {
                customGetList(listTable, idFieldTable)
                    .then(({ data }) => {
                        resolve(formatList(data, valueField));
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve([]);
                    });
            } else {
                if (platformKey === '101' && CACHE[listTable]?.length)
                    return resolve(CACHE[listTable] || []);
                if (platformKey === '107' && CACHE[implementation]?.[listTable]?.length)
                    return resolve(CACHE[implementation][listTable] || []);
                if (!listTable) return resolve([]);
                const listFields = valueField === 'strISO639_1' ? ['strISO639_1'] : [];
                getList({ list: listTable, language, fields: listFields })
                    .then(({ data }) => {
                        const formattedList = formatList(data, valueField);
                        if (platformKey === '101') CACHE[listTable] = formattedList;
                        if (platformKey === '107') {
                            if (!CACHE[implementation]) CACHE[implementation] = {};
                            CACHE[implementation][listTable] = formattedList;
                        }
                        resolve(formattedList);
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve([]);
                    });
            }
        });
        return loadOptions ? loadOptions(pr) : pr;
    }, [
        loadOptions,
        customGetList,
        listTable,
        idFieldTable,
        platformKey,
        implementation,
        language,
        valueField,
    ]);

    const loadOptionsWithTable = shouldLoadTableOptions ? innerLoadOptions : loadOptions;

    useEffect(() => {
        if (!shouldLoadTableOptions || loadOptionsWithFuzzy) return;

        innerLoadOptions().then((options) => {
            if (options?.length) setTableOptions(options);
        });
    }, [innerLoadOptions, loadOptionsWithFuzzy, shouldLoadTableOptions]);

    const loadedOptions = useMemo(() => {
        const baseOptions = localOptions.length > 0 ? localOptions : tableOptions;

        return loadedValues?.length > 0 ? [...loadedValues, ...baseOptions] : baseOptions;
    }, [loadedValues, localOptions, tableOptions]);

    const onClick = () => {
        onClickButtonList && onClickButtonList(value);
    };
    const finalValue = value !== undefined && value !== null ? value : undefined;
    const content = !value?.isEditable ? t('cfm_label_not_editable') : null;
    const resolvedValue = useMemo(
        () => valueObject || resolveListValue(finalValue, loadedOptions),
        [finalValue, loadedOptions, valueObject],
    );

    return (
        <Fragment>
            <Select
                id={id}
                labelMode="horizontal"
                value={resolvedValue}
                isFullWidth={true}
                placeholder={placeholder || t('label_selectone')}
                {...props}
                alwaysLoadOnFocus={props.alwaysLoadOnFocus ?? shouldLoadTableOptions}
                defaultOptions={props.defaultOptions ?? shouldLoadTableOptions}
                options={loadedOptions}
                loadOptions={loadOptionsWithFuzzy || loadOptionsWithTable}
                noOptionsPlaceholder={
                    noOptionsPlaceholder ? t(noOptionsPlaceholder) : t('label_no_options')
                }
            />
            {onClickButtonList && (
                <div className={classes.button}>
                    <Tooltip content={content}>
                        <Button
                            isDisabled={!value?.isEditable}
                            size="small"
                            type="secondary"
                            onClick={onClick}
                        >
                            {t('cfm_label_update_table')}
                        </Button>
                    </Tooltip>
                </div>
            )}
        </Fragment>
    );
}

export default List;
